import React from 'react';

/**
 * AccessRestricted – World-class Tailwind rebuild
 * Glassmorphism card, animated lock icon, neon-green brand accents
 * No MUI dependencies – pure Tailwind buttons
 */
export default function AccessRestricted({ onBack, onRequestAccess }) {
  const handleMail = () => {
    try {
      const subject = encodeURIComponent('Access request: Generate AI Feedback');
      const body = encodeURIComponent(
        'Hi Visist AI team,\n\nI would like access to the Playground feature (Generate AI Feedback) for my coach account.\n\nAccount / Email: ____________________\nOrganization / Academy: ____________________\nUse case: ____________________\n\nThanks!'
      );
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=info@visist.ai&su=${subject}&body=${body}`, '_blank');
    } catch { }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-50"
      style={{
        background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(0,177,64,0.08) 0%, transparent 65%), linear-gradient(160deg, #080f0b 0%, #0a1a10 40%, #060d08 100%)',
      }}
    >
      {/* Background grid dots */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #00b140 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
        aria-hidden="true"
      />

      {/* Floating orbs */}
      <div
        className="absolute w-80 h-80 rounded-full blur-3xl opacity-15 pointer-events-none animate-orb-drift"
        style={{ background: 'radial-gradient(circle, #00b140 0%, transparent 70%)', top: '-5%', left: '-5%' }}
        aria-hidden="true"
      />
      <div
        className="absolute w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none animate-orb-drift"
        style={{ background: 'radial-gradient(circle, #026B33 0%, transparent 70%)', bottom: '-5%', right: '-5%', animationDelay: '4s' }}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden animate-bounce-in"
        style={{
          background: 'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
        role="alert"
        aria-live="polite"
      >
        {/* Neon top border */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent 0%, #00b140 40%, #a3e635 60%, transparent 100%)' }}
        />

        {/* Shimmer sweep */}
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl"
          style={{
            background: 'linear-gradient(105deg, transparent 40%, rgba(0,177,64,0.06) 50%, transparent 60%)',
            animation: 'shimmer 4s linear infinite',
          }}
          aria-hidden="true"
        />

        <div className="relative px-8 pt-10 pb-8 flex flex-col items-center text-center">
          {/* Access Restricted Badge */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-6 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[11px] font-bold tracking-[0.15em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            Access Restricted
          </span>

          {/* Lock icon with pulse ring */}
          <div className="relative mb-6">
            {/* Outer pulse ring */}
            <div
              className="absolute inset-[-12px] rounded-full"
              style={{
                background: 'rgba(0,177,64,0.12)',
                animation: 'pulseRing 2.5s ease-out infinite',
              }}
              aria-hidden="true"
            />
            {/* Inner glow circle */}
            <div
              className="relative flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#00b140]/40"
              style={{
                background: 'radial-gradient(circle, rgba(0,177,64,0.15) 0%, rgba(0,177,64,0.04) 100%)',
                boxShadow: '0 0 24px rgba(0,177,64,0.25)',
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-bounce-in">
                <path d="M6 10V8a6 6 0 1 1 12 0v2" stroke="#00b140" strokeWidth="1.8" strokeLinecap="round" />
                <rect x="4" y="10" width="16" height="10" rx="3" stroke="#00b140" strokeWidth="1.8" />
                <circle cx="12" cy="15" r="2" fill="#00b140" />
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h2
            className="text-xl sm:text-2xl font-black mb-3 leading-tight"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #a3f0c0 60%, #00b140 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            SmartInsights Playground<br />Not Enabled
          </h2>

          <p className="text-sm text-white/50 leading-relaxed mb-8 max-w-xs">
            SmartInsights combines AI-driven video analysis and coach feedback to deliver world-class game insights.
          </p>

          {/* Feature chips */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {['AI Analysis', 'Coach Feedback', 'Rally Insights', 'Heatmaps'].map((f) => (
              <span
                key={f}
                className="px-3 py-1 rounded-full text-[11px] font-semibold text-[#00b140]/80 border border-[#00b140]/25"
                style={{ background: 'rgba(0,177,64,0.07)' }}
              >
                {f}
              </span>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={onRequestAccess || handleMail}
              className="group relative flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-white text-sm transition-all duration-300 hover:scale-105 hover:shadow-[0_0_24px_rgba(0,177,64,0.45)] active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#00b140]/60 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #026B33 0%, #00b140 60%, #038c43 100%)',
                boxShadow: '0 6px 20px rgba(0,177,64,0.35)',
              }}
            >
              {/* Shimmer */}
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)',
                  animation: 'shimmer 1.8s linear infinite',
                }}
                aria-hidden="true"
              />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="relative z-10">
                <path d="M21 2H3l8 9.46V19l6 2V11.46L21 2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="relative z-10">Request Access</span>
            </button>

            <button
              type="button"
              onClick={onBack}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white/70 border border-white/15 transition-all duration-300 hover:border-[#00b140]/50 hover:text-white hover:bg-white/5 active:scale-95 focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Dismiss
            </button>
          </div>

          {/* Note */}
          <p className="mt-6 text-[11px] text-white/30 leading-relaxed">
            If you believe this is a mistake, please sign out and sign in again,{' '}
            or contact{' '}
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=info@visist.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00b140]/70 hover:text-[#00b140] underline underline-offset-2 transition-colors duration-200"
            >
              info@visist.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
